import React, { useEffect } from "react";
import styled from "styled-components";
import { IconX } from "@tabler/icons-react";
import ChatDocumentUpload from "../../stories/ChatDocumentUpload";
import GuideEditor from "../../stories/GuideEditor";
import { Button } from "@mantine/core";
import { useTranslation } from "react-i18next";
import DocumentUpload from "../../stories/DocumentUpload";
import GoogleDrivePickerAndUploader from "../Lib/FilesPickerAndUploader";
import SubcategoryFeedback from "../../stories/SubcategoryFeedback";
import useStartupStore from "../../stores/startupStore";

const DrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
`;

const DrawerContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  transform: translateY(${props => props.isOpen ? '0' : '100%'});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 80vh;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
`;

const DrawerHandle = styled.div`
  width: 40px;
  height: 4px;
  background: #d1d5db;
  border-radius: 2px;
  margin: 12px auto 8px;
  cursor: grab;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

const DrawerTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: #f3f4f6;
  }

  &:active {
    background: #e5e7eb;
  }
`;

const DrawerContent = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  background: #f3f4f6;
  padding: 4px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const TabButton = styled.button`
  flex: 1;
  padding: 8px 16px;
  border: none;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? '#1a1a1a' : '#6b7280'};
  font-size: 14px;
  font-weight: ${props => props.active ? 600 : 500};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'};

  &:active {
    transform: scale(0.98);
  }
`;

const TabContent = styled.div`
  flex-grow: 1;
`;

const MobileBottomDrawer = ({ isOpen, onClose, onCommit, onViewTaskboard }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = React.useState("assistant");
  const [startY, setStartY] = React.useState(0);
  const [currentY, setCurrentY] = React.useState(0);
  const { program, selectedProgram, isOwner } =useStartupStore((state)=> ({
    program: state.program,
    selectedProgram: state.selectedProgram,
    isOwner: state.isOwner,

  }))
  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle swipe to close
  const handleTouchStart = (e) => {
    setStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    const diff = currentY - startY;
    if (diff > 100) { // Swipe down more than 100px to close
      onClose();
    }
    setStartY(0);
    setCurrentY(0);
  };

  return (
    <>
      <DrawerOverlay isOpen={isOpen} onClick={onClose} />
      <DrawerContainer isOpen={isOpen}>
        <DrawerHandle
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        />
        <DrawerHeader>
          <DrawerTitle>Project Tools</DrawerTitle>
          <CloseButton onClick={onClose}>
            <IconX size={20} />
          </CloseButton>
        </DrawerHeader>

        <DrawerContent>
          <TabContainer>
            <TabButton
              active={activeTab === "assistant"}
              onClick={() => setActiveTab("assistant")}
            >
              Assistant
            </TabButton>
            <TabButton
              active={activeTab === "guide"}
              onClick={() => setActiveTab("guide")}
            >
              Guide
            </TabButton>
            <TabButton
              active={activeTab === "files"}
              onClick={() => setActiveTab("files")}
            >
              Files
            </TabButton>
            <TabButton
              active={activeTab === "comments"}
              onClick={() => setActiveTab("comments")}
            >
              Comments
            </TabButton>
          </TabContainer>

          <TabContent activeTab={activeTab}>
            {activeTab === "assistant" && (
              <ChatDocumentUpload isOpen={true} anchored="inline" />
            )}

            {activeTab === "guide" && <GuideEditor />}

            {activeTab === "files" && <GoogleDrivePickerAndUploader />}

            {activeTab === "comments" && (
              <SubcategoryFeedback
                projectOwner={isOwner}
                key={`feedback-${selectedProgram.cid}-${selectedProgram.sid}`}
                subCategoryName={
                  program.categories[selectedProgram.cid].sub_categories[
                    selectedProgram.sid
                  ].name
                }
                subCategoryId={
                  program.categories[selectedProgram.cid].sub_categories[
                    selectedProgram.sid
                  ].id
                }
              />
            )}
          </TabContent>

          {/* <ButtonGroup>
            <Button 
              variant="outline" 
              size="lg" 
              fullWidth
              onClick={onCommit}
            >
              Commit changes
            </Button>
            <Button 
              size="lg" 
              fullWidth
              onClick={onViewTaskboard}
            >
              View Taskboard
            </Button>
          </ButtonGroup> */}
        </DrawerContent>
      </DrawerContainer>
    </>
  );
};

export default MobileBottomDrawer;